import axios from 'axios';
import { ContentstackPage } from '../types/contentstack';
import api from './api';

/**
 * Fetch a page by slug from Contentstack via backend API
 * @param slug - The page slug to fetch
 * @param locale - Optional locale code (e.g., 'en-us', 'fr-ca')
 * @returns Promise with page data
 */
export async function getPageBySlug(slug: string, locale?: string): Promise<ContentstackPage | null> {
  try {
    const url = locale 
      ? `/contentstack/slug/${slug}?locale=${encodeURIComponent(locale)}`
      : `/contentstack/slug/${slug}`;
    const response = await api.get<ContentstackPage>(url);
    return response.data ?? null;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.warn('[Contentstack] Page not found:', slug, locale ? `(locale: ${locale})` : '');
      return null;
    }
    console.error('[Contentstack] Failed to fetch page by slug:', error);
    throw error;
  }
}

/**
 * Fetch translations by locale from Contentstack via backend API
 * @param locale - The locale code (e.g., 'en-us', 'fr-ca')
 * @returns Promise with translations object
 */
export async function getTranslations(locale: string): Promise<Record<string, string>> {
  try {
    const normalizedLocale = locale.toLowerCase();
    const response = await api.get<Record<string, string>>(
      `/contentstack/translations/${normalizedLocale}`
    );
    console.log(`[Contentstack] Successfully fetched translations for locale: ${normalizedLocale}`);
    return response.data ?? {};
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.warn('[Contentstack] Translations not found for locale:', locale);
      return {};
    }
    console.error('[Contentstack] Failed to fetch translations:', error);
    throw error;
  }
}

/**
 * Fetch a menu by type from Contentstack via backend API
 * @param menuType - The menu type to fetch
 * @param locale - Optional locale code (e.g., 'en-us', 'fr-ca')
 * @returns Promise with menu data
 */
export async function getMenuByType<T = any>(menuType: string, locale?: string): Promise<T | null> {
  try {
    const url = locale 
      ? `/contentstack/menu/${menuType}?locale=${encodeURIComponent(locale)}`
      : `/contentstack/menu/${menuType}`;
    const response = await api.get<T>(url);
    return response.data ?? null;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.warn('[Contentstack] Menu not found:', menuType, locale ? `(locale: ${locale})` : '');
      return null;
    }
    console.error('[Contentstack] Failed to fetch menu by type:', error);
    throw error;
  }
}

/**
 * Fetch active announcements from Contentstack via backend API
 * @param locale - Optional locale code (e.g., 'en-us', 'fr-ca')
 * @returns Promise with announcements array
 */
export async function getAnnouncements<T = any>(locale?: string): Promise<T[]> {
  try {
    const url = locale 
      ? `/contentstack/announcements?locale=${encodeURIComponent(locale)}`
      : `/contentstack/announcements`;
    const response = await api.get<T[]>(url);
    return response.data ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.warn('[Contentstack] Announcements not found', locale ? `(locale: ${locale})` : '');
      return [];
    }
    console.error('[Contentstack] Failed to fetch announcements:', error);
    return [];
  }
}
